import {
  Grid,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
} from "@mui/material";
import { MovementTypes } from "constants/index";
import { formatDateOnlyDMY } from "helpers";
import { buildMovementArticleObservationSummary } from "helpers/movementArticleObservations";
import {
  buildOrderArticleObservationWithAssociatedClient,
  sortOrderPreparationArticlesByLocation,
} from "helpers/orderPreparationReports";
import { getMovementArticleStockStateLabel } from "helpers/stockStatus";
import { Client, Movement, TransferPrintItem, User } from "types";

type MovementReportArticle = NonNullable<Movement["articles"]>[number];

type MovementReportColumn = {
  id: string;
  label: string;
  width: string;
  enabled: boolean;
  hasValue?: (article: MovementReportArticle) => boolean;
  render: (article: MovementReportArticle) => string | number;
};

const hasMeaningfulValue = (value: unknown) => {
  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  return value !== null && value !== undefined;
};

export default function OperationPrintDetails({
  movement,
  client,
  users = [],
  hideMovementHeaderDetails,
  title,
}: {
  movement: Movement;
  client: Client | null;
  users?: User[];
  showLote?: boolean;
  showOC?: boolean;
  showFechaVencimiento?: boolean;
  hideMovementHeaderDetails?: boolean;
  title?: string;
}) {
  const isDischargeOrPreparationMovement =
    movement.movement_type_id === MovementTypes.Discharge ||
    movement.movement_type_id === MovementTypes.Preparation;

  const preparedByUser = users.find((u) => u.id === movement.updated_by);
  const createdByUser = users.find((u) => u.id === movement.created_by);

  const isIncome =
    movement.movement_type_id === MovementTypes.Income ||
    movement.movement_type_id === MovementTypes.IncomeDraft ||
    movement.movement_type_id === MovementTypes.IncomeAdjustment;

  const isTransfer = movement.movement_type_id === MovementTypes.Transfer;
  const isPreparation = movement.movement_type_id === MovementTypes.Preparation;
  const movementArticles = movement.articles ?? [];
  const preparationAssociatedClientLabel =
    movement.associated_client?.trim() || null;
  const sortedMovementArticles =
    isPreparation
      ? sortOrderPreparationArticlesByLocation(movementArticles)
      : movementArticles;

  const movementArticleColumns: MovementReportColumn[] = [
    {
      id: "code",
      label: "Cod. del Artí­culo",
      width: "14%",
      enabled: true,
      render: (article) => article.article_locations.article.code,
    },
    {
      id: "description",
      label: "Descripción",
      width: "10%",
      enabled: true,
      render: (article) => article.article_locations.article.description,
    },
    {
      id: "amount",
      label: "Cant.",
      width: "5%",
      enabled: true,
      render: (article) => article.amount,
    },
    {
      id: "amount_per_container",
      label: "Cant. x cont.",
      width: "5%",
      enabled: true,
      render: (article) =>
        article.article_locations?.amount_per_container || "No asignado",
    },
    {
      id: "container",
      label: "Contenedor",
      width: "8%",
      enabled: isIncome,
      hasValue: (article) =>
        hasMeaningfulValue(
          article.article_locations?.article?.container?.description,
        ),
      render: (article) =>
        article.article_locations?.article?.container?.description || "-",
    },
    {
      id: "remaining_amount",
      label: "Cant. Restante",
      width: "10%",
      enabled: isDischargeOrPreparationMovement,
      render: (article) =>
        article.article_locations.stock - article.article_locations.reserved,
    },
    {
      id: "subtotal",
      label: "Subtotal",
      width: "10%",
      enabled: true,
      render: (article) => article.subtotal,
    },
    {
      id: "unit",
      label: "UM",
      width: "8%",
      enabled: true,
      render: (article) => article.article_locations.article.unit.description,
    },
    {
      id: "presentation",
      label: "Presentación",
      width: "8%",
      enabled: true,
      hasValue: (article) => hasMeaningfulValue(article.presentation),
      render: (article) => article.presentation ?? "-",
    },
    {
      id: "client_code",
      label: "Cliente",
      width: "8%",
      enabled: true,
      hasValue: (article) =>
        hasMeaningfulValue(article.article_locations?.article?.client?.code),
      render: (article) =>
        article.article_locations?.article?.client?.code ?? "-",
    },
    {
      id: "location",
      label: "Ubicación",
      width: "10%",
      enabled: true,
      render: (article) => article.article_locations.location.code,
    },
    {
      id: "stock_state",
      label: "Estado mercadería",
      width: "10%",
      enabled: isIncome,
      hasValue: (article) => hasMeaningfulValue(article.stock_state),
      render: (article) => getMovementArticleStockStateLabel(article.stock_state),
    },
    {
      id: "lote",
      label: "Lote",
      width: "10%",
      enabled: client?.show_lote === true,
      hasValue: (article) => hasMeaningfulValue(article.article_locations.lote),
      render: (article) => article.article_locations.lote || "No asignado",
    },
    {
      id: "oc",
      label: "OC",
      width: "10%",
      enabled: client?.show_oc === true,
      hasValue: (article) => hasMeaningfulValue(article.article_locations.oc),
      render: (article) => article.article_locations.oc || "No asignado",
    },
    {
      id: "fecha_vencimiento",
      label: "Fecha Venc.",
      width: "10%",
      enabled: client?.show_fecha_vencimiento === true,
      hasValue: (article) =>
        hasMeaningfulValue(article.article_locations.fecha_vencimiento),
      render: (article) =>
        article.article_locations.fecha_vencimiento
          ? formatDateOnlyDMY(article.article_locations.fecha_vencimiento)
          : "No asignado",
    },
    {
      id: "observation",
      label: "Observación",
      width: "10%",
      enabled: true,
      hasValue: (article) => {
        if (isPreparation) {
          return true;
        }

        const summary = buildMovementArticleObservationSummary(article);
        return hasMeaningfulValue(summary) && summary !== "-";
      },
      render: (article) =>
        isPreparation
          ? buildOrderArticleObservationWithAssociatedClient(
              buildMovementArticleObservationSummary(article),
              preparationAssociatedClientLabel,
            )
          : buildMovementArticleObservationSummary(article),
    },
  ];

  const visibleMovementArticleColumns = movementArticleColumns.filter(
    (column) => {
      if (!column.enabled) {
        return false;
      }

      if (!column.hasValue) {
        return true;
      }

      return sortedMovementArticles.some((article) => column.hasValue?.(article));
    },
  );

  const transferTitle = title || "Transferencia";
  const transferItems: TransferPrintItem[] = Array.isArray(movement.transfer)
    ? (movement.transfer as TransferPrintItem[])
    : [];

  const printTableContainerSx = {
    border: "1px solid grey",
    borderRadius: "0.5rem",
    padding: "1rem",
    "@media print": {
      overflow: "visible",
      breakInside: "auto",
      pageBreakInside: "auto",
    },
  } as const;

  const printTableSx = {
    "@media print": {
      tableLayout: isIncome ? "auto" : "fixed",
      "& thead": {
        display: "table-header-group",
      },
      "& tfoot": {
        display: "table-footer-group",
      },
      "& tbody": {
        breakInside: "auto",
        pageBreakInside: "auto",
      },
      "& tr": {
        breakInside: "avoid",
        pageBreakInside: "avoid",
      },
      "& td, & th": {
        breakInside: "avoid",
        pageBreakInside: "avoid",
      },
    },
  } as const;

  const articlePrintTableSx = {
    ...printTableSx,
    "@media print": {
      ...printTableSx["@media print"],
      transform: "scale(0.90)",
    },
  } as const;

  return (
    <Grid
      container
      spacing={isTransfer ? 1 : 2}
      sx={
        isTransfer && !hideMovementHeaderDetails
          ? {
              "@media print": {
                transform: "scale(0.86)",
                transformOrigin: "top left",
                width: "116.2%",
              },
            }
          : undefined
      }
    >
      {!hideMovementHeaderDetails && (
        <Grid item xs={12}>
          <Grid
            container
            padding={2}
            component={Paper}
            sx={{
              border: "1px solid grey",
              borderRadius: "0.5rem",
            }}
          >
            <Grid item xs={4} display="flex" flexDirection="column" gap={1}>
              <Typography variant="body2">
                <b>Cliente:</b> {client?.razon_social}
              </Typography>
              <Typography variant="body2">
                <b>
                  {movement.movement_type_id === MovementTypes.Transfer
                    ? "Nro. de Planilla"
                    : "Doc. del Cliente"}
                  :
                </b>{" "}
                {movement.client_document}
              </Typography>
              {movement.movement_type_id === MovementTypes.Discharge && (
                <Typography variant="body2">
                  <b>Nro. de Remito:</b> {movement.numero_remito || "-"}
                </Typography>
              )}
            </Grid>
            <Grid item xs={4} display="flex" flexDirection="column" gap={1}>
              {movement.movement_type_id !== MovementTypes.Transfer && (
                <Typography variant="body2">
                  <b>Cliente Asociado:</b> {movement.associated_client}
                </Typography>
              )}
              {movement.movement_type_id !== MovementTypes.Transfer && (
                <Typography variant="body2">
                  <b>Pallets:</b> {movement.pallets}
                </Typography>
              )}
              {movement.movement_type_id === MovementTypes.Transfer && (
                <Typography variant="body2">
                  <b>Preparó:</b>{" "}
                  {preparedByUser
                    ? `${preparedByUser.first_name} ${preparedByUser.last_name}`
                    : "Sin usuario asignado"}
                </Typography>
              )}
            </Grid>
            {movement.movement_type_id !== MovementTypes.Transfer && (
              <Grid item xs={4} display="flex" flexDirection="column" gap={1}>
                <Typography variant="body2">
                  <b>Creado Por:</b>{" "}
                  {createdByUser
                    ? `${createdByUser.first_name} ${createdByUser.last_name}`
                    : "Sin usuario asignado"}
                </Typography>
                <Typography variant="body2">
                  <b>Peso:</b> {movement.weight}
                </Typography>
              </Grid>
            )}
            {movement.observation && (
              <Grid
                item
                xs={12}
                display="flex"
                flexDirection="row"
                gap={1}
                mt={1}
              >
                <Typography variant="body2">
                  <b>Observaciones:</b> {movement.observation}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
      <Grid item xs={12} display="flex" flexDirection="column" gap={2}>
        {sortedMovementArticles.length > 0 && (
          <TableContainer
            aria-label="tabla-motrans-destino"
            component={Paper}
            sx={{ ...printTableContainerSx, width: "100%" }}
          >
            <Typography variant="h6">Artí­culos</Typography>
            <Table size="small" padding="none" sx={articlePrintTableSx}>
              <TableHead>
                <TableRow>
                  {visibleMovementArticleColumns.map((column) => (
                    <TableCell
                      key={column.id}
                      align="center"
                      width={column.width}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedMovementArticles.map((article, index) => (
                  <TableRow key={index}>
                    {visibleMovementArticleColumns.map((column) => (
                      <TableCell key={column.id} align="center">
                        {column.render(article)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {movement.additional_items && movement.additional_items.length > 0 && (
          <TableContainer
            aria-label="tabla-adicionales"
            component={Paper}
            sx={printTableContainerSx}
          >
            <Typography variant="h6">Adicionales</Typography>
            <Table size="small" padding="none" sx={articlePrintTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" width="40%">
                    Descripción
                  </TableCell>
                  <TableCell align="center" width="5%">
                    Cant.
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movement.additional_items.map((additionalItem, index) => (
                  <TableRow key={index}>
                    <TableCell align="center">
                      {additionalItem.additional_item?.description ||
                        additionalItem.description}
                    </TableCell>
                    <TableCell align="center">{additionalItem.amount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {transferItems.length > 0 && (
          <TableContainer
            aria-label="tabla-transferencias"
            component={Paper}
            sx={printTableContainerSx}
          >
            <Typography variant="h5" mb={1}>
              {transferTitle}
            </Typography>
            <Table size="small" padding="none" sx={printTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" width="7%">
                    Producto
                  </TableCell>
                  <TableCell align="center" width="20%">
                    Descripción
                  </TableCell>
                  <TableCell align="center" width="5%">
                    Peso (kg)
                  </TableCell>
                  <TableCell align="center" width="7%">
                    Contenedor
                  </TableCell>
                  <TableCell align="center" width="5%">
                    Cant.
                  </TableCell>
                  <TableCell align="center" width="8%">
                    Desde código
                  </TableCell>
                  <TableCell align="center" width="11%">
                    Desde descripción
                  </TableCell>
                  <TableCell align="center" width="8%">
                    Desde ocupación
                  </TableCell>
                  <TableCell align="center" width="8%">
                    Hacia código
                  </TableCell>
                  <TableCell align="center" width="11%">
                    Hacia descripción
                  </TableCell>
                  <TableCell align="center" width="8%">
                    Hacia ocupación
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transferItems.map((transfer, index) => {
                  const origin = transfer.origin ?? transfer.from;
                  const destination = transfer.destination ?? transfer.to;

                  return (
                    <TableRow key={index}>
                      <TableCell align="center">
                        {origin?.article?.code ?? origin?.article?.id ?? "-"}
                      </TableCell>
                      <TableCell align="center">
                        {origin?.article?.description ?? "-"}
                      </TableCell>
                      <TableCell align="center">
                        {origin?.article?.weight !== undefined &&
                        origin?.article?.weight !== null
                          ? `${origin.article.weight} kg`
                          : "-"}
                      </TableCell>
                      <TableCell align="center">
                        {origin?.article?.container?.description ?? "-"}
                      </TableCell>
                      <TableCell align="center">{transfer.amount ?? "-"}</TableCell>
                      <TableCell align="center">
                        {origin?.location?.code ?? "-"}
                      </TableCell>
                      <TableCell align="center">
                        {origin?.location?.store
                          ? `${origin.location.store.description} | ${origin.location.store.code}`
                          : "-"}
                      </TableCell>
                      <TableCell align="center">
                        {origin?.location?.occupied !== undefined &&
                        origin?.location?.occupied !== null
                          ? `${origin.location.occupied} m3`
                          : "-"}
                      </TableCell>
                      <TableCell align="center">
                        {destination?.location?.code ?? "-"}
                      </TableCell>
                      <TableCell align="center">
                        {destination?.location?.store
                          ? `${destination.location.store.description} | ${destination.location.store.code}`
                          : "-"}
                      </TableCell>
                      <TableCell align="center">
                        {destination?.location?.occupied !== undefined &&
                        destination?.location?.occupied !== null
                          ? `${destination.location.occupied} m3`
                          : "-"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Grid>
    </Grid>
  );
}
